import { useSelector } from '@xstate/react';
import cn from 'classnames';
import type { Duration } from 'dayjs/plugin/duration';
import { useMemo } from 'react';

import { useTextStyle } from '@webapp/common/components/custom-styles';
import { AnswerIcon } from '@webapp/common/components/misc';
import { PointsResults } from '@webapp/common/components/point-results';
import { CssQuestionType } from '@webapp/common/components/survey-custom';
import { Textfield } from '@webapp/common/components/textfield';
import { TimeResults } from '@webapp/common/components/time-results';
import { CompletePageType, TestCheckStatus } from '@webapp/common/resources/survey';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import css from './test-text.css';

export const ListSelectResults: FC<{
    showTimer: boolean;
    factTime: Duration;
    targetTime: Duration;
    maxPoints: number;
    currentPoints: number;
    answers: Array<QuestionAnswerActor>;
    pointsLabel: string;
}> = ({ answers, currentPoints, factTime, maxPoints, pointsLabel, showTimer, targetTime }) => {
    const factOnly = answers.every((answer) => answer.getSnapshot().context.testCheckStatus !== TestCheckStatus.VALID);
    return (
        <div className={css.results}>
            {(maxPoints || currentPoints) && (
                <PointsResults fact={currentPoints} factOnly={factOnly} max={maxPoints} pointsLabel={pointsLabel} />
            )}
            {showTimer && <TimeResults factTime={factTime} targetTime={targetTime} />}
        </div>
    );
};
export const TestText: VFC<SurveyQuestion> = ({ question, readOnly }) => {
    const { answersActors, currentPoints, factTime, maxPoints, responseActor, targetTime, id: questionId } = question;
    const {
        info: {
            params: {
                main: { completePage },
                tests: { scoreLabel },
            },
        },
    } = useStateSelector((ctx) => ctx);
    const response = useSelector(responseActor, (r) => r.context);
    const redirect = [CompletePageType.REDIRECT_TO_WEBSITE, CompletePageType.TO_SURVEY].includes(completePage);
    const showText = typeof maxPoints === 'number' || typeof currentPoints === 'number';
    const showResults = !redirect && Boolean(factTime || targetTime || maxPoints || currentPoints) && readOnly;

    const showTimer = Boolean(factTime || targetTime);
    const { inputStyle, itemsGap } = useTextStyle();

    const listStyle = useMemo(
        () => ({
            gap: itemsGap,
        }),
        [itemsGap],
    );

    return (
        <div className={css.testText} style={listStyle}>
            <div className={cn(CssQuestionType.SHORT_TEXT, css.fullWidth)}>
                {showResults || showText ? (
                    <div className={css.checked}>
                        {showResults && (
                            <AnswerIcon
                                checked={true}
                                testCheckStatus={
                                    maxPoints === currentPoints ? TestCheckStatus.VALID : TestCheckStatus.INVALID
                                }
                            />
                        )}
                        {response.value || ''}
                    </div>
                ) : (
                    <Textfield
                        transparent
                        style={inputStyle}
                        value={(response.value as string) || ''}
                        onChange={(v: any) => void responseActor.send({ type: 'CHANGE', value: v, questionId })}
                    />
                )}
            </div>
            {showResults && (
                <ListSelectResults
                    answers={answersActors}
                    currentPoints={currentPoints}
                    factTime={factTime}
                    maxPoints={maxPoints}
                    showTimer={showTimer}
                    targetTime={targetTime}
                    pointsLabel={scoreLabel}
                />
            )}
        </div>
    );
};
